import React, { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import imageCompression from 'browser-image-compression';
import {
  CloseOutlined, WhatsAppOutlined, UploadOutlined, BankOutlined, InfoCircleOutlined,
  InboxOutlined, DeleteOutlined, CopyOutlined, CheckOutlined, MobileOutlined, ZoomInOutlined,
} from '@ant-design/icons';
import type { CarritoItem } from '../../../store/Carrito/types';
import type { EntregaRequest, PedidoResponse } from '../../../store/MSCommerce/Pedidos/types';
import { createPedido } from '../../../store/MSCommerce/Pedidos/thunks';
import deunaQr from '../../../globals/images/Layout/QrDeUna.jpeg';
import Contact from '../../../Services/contact';
import { crearMensajePedidoWhatsapp } from '../../../Services/pedidoUtils';
import styles from '../Ofertas/Ofertas.module.scss';

interface ConfirmacionPedidoModalProps {
  items: CarritoItem[];
  entrega: EntregaRequest;
  total: number;
  onClose: () => void;
  onReservado: () => void;
}

// ── Cuentas bancarias ────────────────────────────────────────────
// Fijas en el código (así lo decidiste). Edita aquí si cambian de banco/número.
// "color" es solo el acento visual de cada tarjeta, para diferenciarlas de un vistazo.
interface CuentaBancaria {
  banco: string;
  tipo: string;
  numero: string;
  titular: string;
  identificacion: string;
  correo?: string;
  color: string;
}

const CUENTAS_BANCARIAS: CuentaBancaria[] = [
  {
    banco: 'Banco Pichincha',
    tipo: 'Cuenta de Ahorros',
    numero: '2207712434',
    titular: 'Samira Chisaguano',
    identificacion: '1726877341',
    color: '#F2A900',
  },
  {
    banco: 'Banco Guayaquil',
    tipo: 'Cuenta de Ahorros',
    numero: '0060965523',
    titular: 'Samira Alejandra Chisaguano Maldonado',
    identificacion: '1726877341',
    correo: 'samirachisaguano@gmail.com',
    color: '#A6192E',
  },
];

const DEUNA_COLOR = '#FF5A5F';

const getCodigoCorto = (id: string) => `GS-${id.slice(0, 8).toUpperCase()}`;

const TIPO_ENTREGA_LABEL: Record<string, string> = {
  PUNTO_ENTREGA: 'Punto de entrega (entrega personal)',
  DOMICILIO: 'Envío a domicilio',
  NACIONAL: 'Envío nacional',
};

const ENTREGA_REQUIERE_COTIZACION = new Set(['DOMICILIO', 'NACIONAL']);

export const ConfirmacionPedidoModal: React.FC<ConfirmacionPedidoModalProps> = ({ items, entrega, total, onClose, onReservado }) => {
  const dispatch = useDispatch<any>();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [comprobanteFile, setComprobanteFile] = useState<File | null>(null);
  const [comprobantePreview, setComprobantePreview] = useState<string | null>(null);
  const [montoPagado, setMontoPagado] = useState('');
  const [reservando, setReservando] = useState(false);
  const [pedidoCreado, setPedidoCreado] = useState<PedidoResponse | null>(null);
  const [copiado, setCopiado] = useState<string | null>(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const costoEnvio = entrega.costoEnvio ?? 0;
  const envioPorConfirmar = ENTREGA_REQUIERE_COTIZACION.has(entrega.tipo) && costoEnvio <= 0;
  const totalReserva = total + costoEnvio;
  const montoPagadoNumero = Number(montoPagado);
  const montoPagadoValido = Boolean(
    montoPagado.trim() &&
    Number.isFinite(montoPagadoNumero) &&
    montoPagadoNumero > 0 &&
    montoPagadoNumero <= totalReserva
  );
  const saldoPendiente = Math.max(0, Math.round((totalReserva - montoPagadoNumero) * 100) / 100);

  const handleCopy = (texto: string, key: string) => {
    navigator.clipboard.writeText(texto);
    setCopiado(key);
    setTimeout(() => setCopiado(null), 1500);
  };

  const renderCuentaCard = (cuenta: typeof CUENTAS_BANCARIAS[number], i: number) => (
    <div key={i} className={styles.modal__paymentCard} style={{ borderLeftColor: cuenta.color, flex: '1 1 260px' }}>
      <div className={styles.modal__paymentHeader}>
        <span className={styles.modal__paymentIcon} style={{ background: cuenta.color }}>
          <BankOutlined />
        </span>
        {cuenta.banco}
      </div>

      <div className={styles.modal__paymentRow}>
        <span className={styles.modal__paymentLabel}>Tipo de cuenta</span>
        <span className={styles.modal__paymentValue}>{cuenta.tipo}</span>
      </div>
      <div className={styles.modal__paymentRow}>
        <span className={styles.modal__paymentLabel}>Número de cuenta</span>
        <span className={styles.modal__paymentValue}>
          {cuenta.numero}
          <button
            type="button"
            className={styles.modal__copyBtn}
            onClick={() => handleCopy(cuenta.numero, `numero-${i}`)}
          >
            {copiado === `numero-${i}` ? <CheckOutlined /> : <CopyOutlined />}
            {copiado === `numero-${i}` ? 'Copiado' : 'Copiar'}
          </button>
        </span>
      </div>
      <div className={styles.modal__paymentRow}>
        <span className={styles.modal__paymentLabel}>Titular</span>
        <span className={styles.modal__paymentValue}>{cuenta.titular}</span>
      </div>
      <div className={styles.modal__paymentRow}>
        <span className={styles.modal__paymentLabel}>Cédula / RUC</span>
        <span className={styles.modal__paymentValue}>{cuenta.identificacion}</span>
      </div>
      {cuenta.correo && (
        <div className={styles.modal__paymentRow}>
          <span className={styles.modal__paymentLabel}>Correo</span>
          <span className={styles.modal__paymentValue}>{cuenta.correo}</span>
        </div>
      )}
    </div>
  );

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setComprobanteFile(file);
    setComprobantePreview(URL.createObjectURL(file));
  };

  const handleRemoveFile = () => {
    setComprobanteFile(null);
    setComprobantePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const toBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const handleReservar = async () => {
    if (!comprobanteFile || !montoPagadoValido) return;
    setReservando(true);
    const comprimida = await imageCompression(comprobanteFile, {
      maxSizeMB: 0.5,
      maxWidthOrHeight: 1600,
      useWebWorker: true,
    });
    const base64 = await toBase64(comprimida);
    const pedido = await dispatch(createPedido({
      items: items.map(i => ({ productoId: i.productoId, cantidad: i.cantidad })),
      entrega,
      base64Imagen: base64,
      montoPagado: Number(montoPagado),
    }));
    setReservando(false);
    if (pedido) {
      setPedidoCreado(pedido);
      onReservado();
    }
  };

  if (pedidoCreado) {
    const codigo = getCodigoCorto(pedidoCreado.id);
    const mensajeWhatsapp = crearMensajePedidoWhatsapp(pedidoCreado);

    return (
      <div className={styles.modal__overlay}>
        <div className={styles.modal__panel} onClick={e => e.stopPropagation()}>
          <div className={styles.modal__header}>
            <div className={styles.modal__headerLeft}>
              <h2 className={styles.modal__title}>¡Tu pedido {codigo} fue creado!</h2>
            </div>
            <button className={styles.modal__close} onClick={onClose}><CloseOutlined /></button>
          </div>

          <div className={styles.modal__body}>
            <p style={{ fontSize: 14, color: '#739A7C', fontWeight: 600, marginTop: 0 }}>
              Comprobante recibido. Lo revisaremos pronto y te confirmaremos por WhatsApp.
            </p>

            <a
              href={`https://wa.me/${Contact.WHATSAPP_NUMERO}?text=${mensajeWhatsapp}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                padding: '11px',
                background: 'transparent',
                border: '1.5px solid #25D366',
                color: '#25D366',
                borderRadius: 50,
                fontWeight: 700,
                textDecoration: 'none',
                marginTop: 16,
              }}
            >
              <WhatsAppOutlined /> Escríbenos por WhatsApp
            </a>
          </div>

          <div className={styles.modal__footer}>
            <button className={styles.modal__submit} onClick={onClose}>Listo</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.modal__overlay}>
      <div className={styles.modal__panel} onClick={e => e.stopPropagation()}>

        <div className={styles.modal__header}>
          <div className={styles.modal__headerLeft}>
            <h2 className={styles.modal__title}>Confirma tu reserva</h2>
          </div>
          <button className={styles.modal__close} onClick={onClose}><CloseOutlined /></button>
        </div>

        <div className={styles.modal__body}>
          <div className={styles.modal__summary}>
            <div className={styles.modal__summaryHeader}>
              <div>
                <p className={styles.modal__summaryEyebrow}>Resumen de tu reserva</p>
                <h3 className={styles.modal__summaryTitle}>Revisa tus datos antes de continuar</h3>
              </div>
            </div>

            <div className={styles.modal__summaryProducts}>
              {items.map(item => (
                <div key={item.productoId} className={styles.modal__summaryProduct}>
                  <span>
                    <strong>{item.nombre}</strong>
                    <small>{item.cantidad} × ${item.precio.toFixed(2)}</small>
                  </span>
                  <strong>${(item.precio * item.cantidad).toFixed(2)}</strong>
                </div>
              ))}
            </div>

            <div className={styles.modal__summaryDelivery}>
              <span className={styles.modal__summaryLabel}>Entrega</span>
              <strong>{TIPO_ENTREGA_LABEL[entrega.tipo] ?? entrega.tipo}</strong>
              {entrega.contactoNombre && <small>Contacto: {entrega.contactoNombre}</small>}
              {entrega.contactoPhone && <small>WhatsApp: {entrega.contactoPhone}</small>}
              {entrega.direccion && <small>Dirección: {entrega.direccion}</small>}
              {entrega.ciudad && <small>Ciudad: {entrega.ciudad}</small>}
              {entrega.referencia && <small>Referencia: {entrega.referencia}</small>}
              {envioPorConfirmar && (
                <small className={styles.modal__summaryShippingNote}>
                  El costo de envío se coordinará por WhatsApp según la dirección y la ciudad.
                </small>
              )}
            </div>

            <div className={styles.modal__summaryTotals}>
              <div>
                <span>{envioPorConfirmar ? 'Total de productos' : 'Total'}</span>
                <strong>${totalReserva.toFixed(2)}</strong>
              </div>
              {costoEnvio > 0 && (
                <div>
                  <span>Costo de envío</span>
                  <strong>${costoEnvio.toFixed(2)}</strong>
                </div>
              )}
              <div className={styles.modal__summaryPayment}>
                <label htmlFor="monto-pagado-reserva">Abono que vas a registrar</label>
                <input
                  id="monto-pagado-reserva"
                  className={styles.modal__summaryPaymentInput}
                  type="number"
                  min={0.01}
                  step="0.01"
                  placeholder="2.00"
                  value={montoPagado}
                  onChange={e => setMontoPagado(e.target.value)}
                />
              </div>
              <div className={styles.modal__summaryBalance}>
                <span>{envioPorConfirmar ? 'Saldo pendiente de productos' : 'Saldo pendiente'}</span>
                <strong>
                  {!montoPagado.trim()
                    ? 'Por calcular'
                    : montoPagadoValido
                      ? `$${saldoPendiente.toFixed(2)}`
                      : 'Revisa el abono'}
                </strong>
              </div>
            </div>
          </div>

          <p style={{ fontSize: 14, color: '#3A302E', marginTop: 18 }}>
            Realiza el abono a una de estas cuentas y sube el comprobante para reservar tus productos.
          </p>

          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 10,
              background: '#FFF4E5',
              border: '1.5px solid #E2BB84',
              borderRadius: 10,
              padding: '14px 16px',
              marginBottom: 18,
            }}
          >
            <InfoCircleOutlined style={{ color: '#9C7044', fontSize: 20, marginTop: 2 }} />
            <p style={{ margin: 0, fontSize: 13.5, color: '#3A302E', lineHeight: 1.5 }}>
              <strong style={{ color: '#171516' }}>No necesitas pagar todo de una vez.</strong> Con un abono
              desde <strong style={{ color: '#171516' }}>$2</strong> ya puedes reservar tus productos y coordinar
              el resto contra entrega.
            </p>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 20 }}>
            {/* Pichincha y DeUna van juntos porque DeUna es un producto de Banco Pichincha */}
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
              {renderCuentaCard(CUENTAS_BANCARIAS[0], 0)}

              <div className={styles.modal__paymentCard} style={{ borderLeftColor: DEUNA_COLOR, flex: '1 1 200px' }}>
                <div className={styles.modal__paymentHeader}>
                  <span className={styles.modal__paymentIcon} style={{ background: DEUNA_COLOR }}>
                    <MobileOutlined />
                  </span>
                  DeUna
                </div>
                <div className={styles.modal__deunaBody}>
                  <img src={deunaQr} alt="QR para pagar con DeUna" className={styles.modal__deunaQr} />
                  <p className={styles.modal__deunaText}>Escanea este código con tu app DeUna</p>
                </div>
              </div>
            </div>

            {renderCuentaCard(CUENTAS_BANCARIAS[1], 1)}
          </div>

          {montoPagado.trim() && !montoPagadoValido && (
            <p className={styles.modal__summaryError}>
              El abono debe ser mayor que cero y no superar el total mostrado.
            </p>
          )}

          <div className={styles.modal__field} style={{ marginBottom: 16 }}>
            <label className={styles.modal__label}>Subir comprobante de pago *</label>

            {!comprobanteFile ? (
              <>
                <div
                  className={styles.modal__dropzone}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <InboxOutlined className={styles.modal__dropIcon} />
                  <p className={styles.modal__dropText}>Arrastra una imagen o <span>haz clic</span></p>
                  <p className={styles.modal__dropHint}>PNG, JPG, WEBP</p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                  />
                </div>
                <p style={{ fontSize: 12, color: '#7D6A61', marginTop: 6 }}>
                  Es obligatorio adjuntar el comprobante para poder reservar.
                </p>
              </>
            ) : (
              <div className={styles.modal__previewItem}>
                <img
                  src={comprobantePreview ?? undefined}
                  alt="Comprobante"
                  className={styles.modal__previewImg}
                  onClick={() => setLightboxOpen(true)}
                  title="Ver imagen completa"
                />
                <span className={styles.modal__previewZoomHint} onClick={() => setLightboxOpen(true)}>
                  <ZoomInOutlined />
                </span>
                <button
                  type="button"
                  className={styles.modal__removeImg}
                  onClick={handleRemoveFile}
                  title="Quitar imagen"
                >
                  <DeleteOutlined />
                </button>
              </div>
            )}
          </div>
        </div>

        <div className={styles.modal__footer}>
          <button className={styles.modal__cancel} onClick={onClose}>Cancelar</button>
          <button
            className={styles.modal__submit}
            disabled={!comprobanteFile || !montoPagadoValido || reservando}
            onClick={handleReservar}
          >
            <UploadOutlined /> {reservando ? 'Reservando...' : 'Reservar productos'}
          </button>
        </div>

      </div>

      {lightboxOpen && comprobantePreview && (
        <div className={styles.modal__lightbox} onClick={() => setLightboxOpen(false)}>
          <button
            type="button"
            className={styles.modal__lightboxClose}
            onClick={() => setLightboxOpen(false)}
            title="Cerrar"
          >
            <CloseOutlined />
          </button>
          <img src={comprobantePreview} alt="Comprobante ampliado" className={styles.modal__lightboxImg} />
        </div>
      )}
    </div>
  );
};
